import type { CardapioCatalogo, CartItem, ClienteCardapio, ContaItem } from "./types";
import type { TranslationItem } from "./translation";

/** Lotes enviados ao endpoint de tradução (timeout de hospedagem compartilhada). */
export const CARDAPIO_TRANSLATION_BATCH_SIZE = 10;

export class CardapioApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function handleJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed) {
    if (res.ok) return {} as T;
    throw new Error("Erro na requisição");
  }
  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch {
    throw new Error("Resposta inválida do servidor");
  }
  if (!res.ok || (data && typeof data === "object" && "ok" in data && (data as { ok?: boolean }).ok === false)) {
    const obj = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
    const msg =
      obj && typeof obj.error === "string"
        ? obj.error
        : obj && typeof obj.message === "string"
          ? obj.message
          : "Erro na requisição";
    throw new CardapioApiError(msg, res.status);
  }
  return data as T;
}

export const cardapioApi = {
  async atendimentoAberto(mesa: string, nome: string, signal?: AbortSignal): Promise<boolean> {
    const query = new URLSearchParams({ mesa, cliente_nome: nome });
    const res = await fetch(`/api/cardapio/atendimento-aberto.php?${query}`, { signal });
    const data = await handleJson<{ existe: boolean }>(res);
    return data.existe === true;
  },
  async buscarAtendimentoAberto(
    mesa: string,
    nome: string,
    signal?: AbortSignal,
  ): Promise<{ existe: boolean; comanda_id?: number; mesa?: string; cliente_nome?: string }> {
    const query = new URLSearchParams({ mesa, cliente_nome: nome });
    const res = await fetch(`/api/cardapio/atendimento-aberto.php?${query}`, { signal });
    return handleJson(res);
  },
  async catalogo(opts?: { compact?: boolean }): Promise<CardapioCatalogo> {
    const qs = opts?.compact ? "?compact=1" : "";
    const res = await fetch(`/api/cardapio/catalogo.php${qs}`);
    const data = await handleJson<{ categorias: CardapioCatalogo["categorias"]; promocoes: CardapioCatalogo["promocoes"]; empresa: CardapioCatalogo["empresa"] }>(res);
    return {
      empresa: data.empresa ?? { nome: "", logo_url: null },
      categorias: data.categorias ?? [],
      promocoes: data.promocoes ?? [],
    };
  },

  async enviarPedido(payload: {
    mesa: string;
    cliente_nome: string;
    cliente_whatsapp?: string;
    comanda_id?: number;
    confirmar_adicao?: boolean;
    itens: Array<{ produto_id: number; quantidade: number; observacao?: string }>;
  }): Promise<{ comanda_id: number; mesa: string; cliente_nome?: string; atendimento_aberto?: boolean }> {
    const res = await fetch("/api/cardapio/pedido.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await handleJson<{ comanda_id: number; mesa: string; cliente_nome?: string; atendimento_aberto?: boolean }>(res);
    return {
      comanda_id: data.comanda_id,
      mesa: data.mesa,
      cliente_nome: data.cliente_nome,
      atendimento_aberto: data.atendimento_aberto !== false,
    };
  },

  async conta(params: { mesa?: string; cliente_nome?: string; cliente_whatsapp?: string; comanda_id?: number }): Promise<{
    comanda_id?: number | null;
    atendimento_aberto?: boolean;
    cliente_nome?: string | null;
    mesa?: string;
    nome?: string;
    itens?: ContaItem[];
    subtotal?: number;
    total?: number;
    itens_mesa: ContaItem[];
    itens_individuais: ContaItem[];
    subtotal_mesa: number;
    total_mesa: number;
    subtotal_individual: number;
    total_individual: number;
  }> {
    const qs = new URLSearchParams();
    if (params.mesa) qs.set("mesa", params.mesa);
    if (params.comanda_id) qs.set("comanda_id", String(params.comanda_id));
    if (params.cliente_nome) qs.set("cliente_nome", params.cliente_nome);
    if (params.cliente_whatsapp) qs.set("cliente_whatsapp", params.cliente_whatsapp);
    const res = await fetch(`/api/cardapio/conta.php?${qs}`);
    return handleJson(res);
  },

  async status(params: {
    mesa?: string;
    cliente_nome?: string;
    cliente_whatsapp?: string;
    comanda_nome?: string;
  }): Promise<{
    comanda_ativa: boolean;
    cliente_itens_restantes: number;
    mesa_finalizada: boolean;
    cliente_sem_itens: boolean;
    comanda_nome_finalizada: boolean | null;
  }> {
    const qs = new URLSearchParams();
    if (params.mesa) qs.set("mesa", params.mesa);
    if (params.cliente_nome) qs.set("cliente_nome", params.cliente_nome);
    if (params.cliente_whatsapp) qs.set("cliente_whatsapp", params.cliente_whatsapp);
    if (params.comanda_nome) qs.set("comanda_nome", params.comanda_nome);
    const res = await fetch(`/api/cardapio/status.php?${qs}`);
    return handleJson(res);
  },

  async fecharIndividual(payload: {
    mesa: string;
    cliente_nome: string;
    cliente_whatsapp: string;
    item_ids: number[];
  }): Promise<{ nova_comanda_nome: string; nova_comanda_id: number }> {
    const res = await fetch("/api/cardapio/fechar-individual.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return handleJson(res);
  },

  async traduzirBatch(payload: {
    target: "en" | "es";
    items: TranslationItem[];
  }): Promise<{ translations: Record<string, string> }> {
    const { target, items } = payload;
    if (items.length === 0) {
      return { translations: {} };
    }
    if (items.length > CARDAPIO_TRANSLATION_BATCH_SIZE) {
      throw new Error(`Lote de tradução excede ${CARDAPIO_TRANSLATION_BATCH_SIZE} itens`);
    }

    const res = await fetch("/api/cardapio/traduzir.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target, items }),
    });
    const data = await handleJson<{ translations?: Record<string, string> }>(res);
    return { translations: data.translations ?? {} };
  },

  async solicitarGarcom(payload: {
    cliente_nome: string;
    cliente_telefone?: string;
    mesa: string;
    comanda_id?: number;
    idempotency_key: string;
    opcoes: {
      copos: number;
      talheres: number;
      pratos: number;
      limpar_mesa: boolean;
      observacao: string;
    };
  }): Promise<void> {
    const res = await fetch("/api/cardapio/garcom-solicitar.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await handleJson(res);
  },

  async solicitarFecharConta(payload: {
    cliente_nome?: string;
    cliente_telefone?: string;
    mesa?: string;
    comanda_id?: number;
    idempotency_key: string;
    modo: "mesa" | "individual" | "selecionados";
    pagamento: "no_local";
    item_ids?: number[];
    valor_total?: number;
  }): Promise<void> {
    const res = await fetch("/api/cardapio/solicitar-fechar-conta.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await handleJson(res);
  },
};

export type { ClienteCardapio, CartItem, ContaItem };
